import { createElement, useId, useState, type ReactElement } from "react";
import { Button, Feedback, FormField, Input } from "@zzk/ui";
import { AuthFrame } from "@zzk/admin-pages";
import { brandConfig } from "../../../brand/index.js";

export interface CodeEntryFormProps {
  readonly flowIdHint?: string;
  readonly emailHint?: string;
}

interface SubmitState {
  readonly kind: "idle" | "submitting" | "error";
  readonly message?: string;
  readonly remaining_attempts?: number;
}

const authBrand = {
  name: brandConfig.name,
  logoSrc: brandConfig.assets.logo.src,
  logoAlt: brandConfig.assets.logo.alt,
};

export function CodeEntryForm(props: CodeEntryFormProps): ReactElement {
  const codeId = useId();
  const [code, setCode] = useState("");
  const [state, setState] = useState<SubmitState>({ kind: "idle" });

  const flowId =
    props.flowIdHint ??
    (typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("flow_id") ?? ""
      : "");

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!code || code.length !== 6 || !flowId) return;
    setState({ kind: "submitting" });
    try {
      const response = await fetch("/api/lens/auth/code/verify", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          flow_id: flowId,
          code,
        }),
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as {
          error?: { kind?: string; remaining_attempts?: number };
        };
        const kind = body.error?.kind;
        if (kind === "max_attempts_exceeded" || kind === "flow_expired") {
          window.location.href = "/app/login?error=max_attempts";
          return;
        }
        setState({
          kind: "error",
          message: friendlyError(kind, response.status),
          remaining_attempts: body.error?.remaining_attempts,
        });
        return;
      }
      window.location.href = "/app/overview";
    } catch {
      setState({
        kind: "error",
        message: "Não foi possível verificar o código. Tente novamente.",
      });
    }
  }

  const helper =
    state.remaining_attempts !== undefined
      ? `Restam ${state.remaining_attempts} tentativa(s).`
      : undefined;

  return AuthFrame({
    title: "Digite o código",
    brand: authBrand,
    description: props.emailHint
      ? `Enviamos um código de 6 dígitos para ${maskEmail(props.emailHint)}.`
      : "Verifique seu email e digite o código de 6 dígitos recebido.",
    status:
      state.kind === "error"
        ? Feedback({
            type: "error",
            title: "Código não verificado",
            message: state.message ?? "Tente novamente.",
          })
        : null,
    children: createElement(
      "form",
      {
        onSubmit,
        "data-testid": "code-entry-form",
        noValidate: true,
        className: "grid gap-4",
      },
      FormField({
        id: codeId,
        name: "code",
        label: "Código de 6 dígitos",
        required: true,
        hint: helper,
        control: Input({
          id: codeId,
          name: "code",
          type: "text",
          inputMode: "numeric",
          autoComplete: "one-time-code",
          placeholder: "000000",
          value: code,
          maxLength: 6,
          pattern: "\\d{6}",
          onChange: (event) => {
            const next = (event.target as HTMLInputElement).value.replace(
              /\D/g,
              "",
            );
            setCode(next.slice(0, 6));
          },
          required: true,
          disabled: state.kind === "submitting",
        }),
      }),
      createElement(
        "div",
        {
          className: "flex flex-wrap items-center justify-between gap-2",
          key: "actions",
        },
        createElement(
          Button,
          {
            type: "submit",
            variant: "primary",
            loading: state.kind === "submitting",
            loadingLabel: "Verificando…",
            disabled:
              state.kind === "submitting" || code.length !== 6 || !flowId,
            key: "submit",
          },
          "Verificar",
        ),
        createElement(
          "a",
          {
            href: "/app/login",
            "data-testid": "resend-code-link",
            className: "zzk-text-sm zzk-text-muted",
            key: "resend",
          },
          "Solicitar novo código",
        ),
      ),
    ),
  });
}

function friendlyError(kind: string | undefined, status: number): string {
  if (kind === "invalid_code") {
    return "Código inválido. Confira e tente novamente.";
  }
  if (kind === "upstream_unavailable" || status === 503) {
    return "Serviço de autenticação indisponível no momento.";
  }
  if (kind === "invalid_body") {
    return "Dados inválidos. Confira o código e tente novamente.";
  }
  return "Não foi possível verificar o código.";
}

function maskEmail(email: string): string {
  const [local, domain] = email.split("@");
  if (!local || !domain) return email;
  const visible =
    local.length <= 2
      ? local
      : `${local[0]}${"*".repeat(Math.max(1, local.length - 2))}${local.slice(-1)}`;
  return `${visible}@${domain}`;
}
